import React, { useState, useEffect } from 'react';
import { Input, Select, Button, message, Tooltip, Spin } from 'antd';
import { DocumentIcon, SparklesIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';


import { InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const AutomaticDataEntryRequirementsChecklist = ({ onAddRow, onDataSubmitted }) => {


  const [companyOptions, setCompanyOptions] = useState([]);

  const quarterOptions = ['All', 'Q1', 'Q2', 'Q3', 'Q4'].map(q => ({ value: q, label: q }));
  const stakeholderOptions = [
    'Plan Sponsor', 
    'Participants', 
    'TPA', 
    'Recordkeeper'
  ].map(s => ({ value: s, label: s }));

  // Add isRewriting state
  const [isRewriting, setIsRewriting] = useState(false);

  // Add this helper function at the top of the component to get current quarter
  const getCurrentQuarter = () => {
    const month = new Date().getMonth(); // 0-11
    if (month < 3) return 'Q1';
    if (month < 6) return 'Q2';
    if (month < 9) return 'Q3';
    return 'Q4';
  };

  // Update the initial formData state with current quarter
  const [formData, setFormData] = useState({
    company_name: '',
    quarter: getCurrentQuarter(), // Set default quarter
    reporting_year: 'All', // Default to "All" for reporting year
    requirement: '',
    stakeholder: '',
    completed: 'No',
    due_dates: {},
    priority: 'Medium',
    add_to_annual_plan_review: 'No',
    include_in_next_years_master_spreadsheet: 'No'
  });

  // Fetch client names when component mounts
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');
       const year = dayjs().year().toString(); // Ensure year is a string
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${year}`, {
          headers: {
            'user-id': userId
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        // First, create options array without "All Clients"
        let options = data.client_names.map(name => ({
          value: name,
          label: name
        }));
        
        // Sort the company names alphabetically
        options.sort((a, b) => a.label.localeCompare(b.label));
        
        // Add "All Clients" at the beginning
        options.unshift({ value: 'All Clients', label: 'All Clients' });
        
        setCompanyOptions(options);
      } catch (error) {
        console.error('Error fetching client names:', error);
        message.error('Failed to load client list');
      }
    };

    fetchClientNames();
    
    // Set default due date for current quarter
    const currentQuarter = getCurrentQuarter();
    const quarterEndDate = getQuarterEndDate(currentQuarter);
    setFormData(prev => ({
      ...prev,
      due_dates: { [currentQuarter]: quarterEndDate }
    }));
  }, []);

  // Add this helper function at the top of the component
  const getQuarterEndDate = (quarter) => {
    const currentYear = new Date().getFullYear().toString(); // Convert to string
    switch (quarter) {
      case 'Q1':
        return `${currentYear}-03-31`;
      case 'Q2':
        return `${currentYear}-06-30`;
      case 'Q3':
        return `${currentYear}-09-30`;
      case 'Q4':
        return `${currentYear}-12-31`;
      default:
        return '';
    }
  };

  // Update the handleChange function to set default due date when quarter changes
  const handleChange = (name, value) => {
    console.log(`Field "${name}" changed to:`, value);
    
    if (name === 'company_name') {
      if (value === 'All Clients') {
        console.log('Setting "All Clients" with completed = No');
        setFormData(prev => ({ 
          ...prev, 
          [name]: value,
          completed: 'No'
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else if (name === 'quarter') {
      if (value === 'All') {
        // For "All" quarters, set a special due date with "All" as the key
        const currentYear = new Date().getFullYear().toString(); // Convert to string
        const defaultDueDate = `${currentYear}-12-31`; // Use end of year as default
        setFormData(prev => ({
          ...prev,
          [name]: value,
          due_dates: { "All": defaultDueDate }
        }));
      } else {
        // When specific quarter changes, set the default due date to quarter end
        const quarterEndDate = getQuarterEndDate(value);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          due_dates: { [value]: quarterEndDate }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDueDateChange = (quarter, date) => {
    console.log(`Due date for ${quarter} changed to:`, date);
    setFormData(prev => ({
      ...prev,
      due_dates: {
        ...prev.due_dates,
        [quarter]: date
      }
    }));
  };

  // Add handleRewriteText function
  const handleRewriteText = async () => {
    if (!formData.requirement.trim()) {
      message.warning('Please enter some text to rewrite');
      return;
    }

    setIsRewriting(true);
    try {
      const response = await fetch('https://plansync-backend-234288438538.us-central1.run.app/api/rewrite_touchpoint_text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: formData.requirement })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rewrittenText = await response.json();
      
      // Add a slight delay for smoother animation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setFormData({ ...formData, requirement: rewrittenText });
      message.success('Text rewritten successfully');
    } catch (error) {
      console.error('Error rewriting text:', error);
      message.error('Failed to rewrite text');
    } finally {
      setIsRewriting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const userId = localStorage.getItem('userid');
     const year = dayjs().year().toString(); // Ensure year is a string

      // Log current form state
      console.log('Current Form Data:', {
        company_name: formData.company_name,
        quarter: formData.quarter,
        reporting_year: formData.reporting_year,
        requirement: formData.requirement,
        stakeholder: formData.stakeholder,
        completed: formData.completed,
        due_dates: formData.due_dates,
        priority: formData.priority,
        add_to_annual_plan_review: formData.add_to_annual_plan_review,
        include_in_next_years_master_spreadsheet: formData.include_in_next_years_master_spreadsheet
      });

      // Transform the data to match the expected API format
      const transformedData = {
        company_name: formData.company_name,
        quarter: formData.quarter,
        reporting_year: formData.reporting_year,
        requirement: formData.requirement,
        stakeholder: formData.stakeholder,
        completed: formData.company_name === 'All Clients' ? 'No' : formData.completed,
        due_dates: formData.due_dates,
        priority: formData.priority,
        add_to_annual_plan_review: formData.add_to_annual_plan_review,
        include_in_next_years_master_spreadsheet: formData.include_in_next_years_master_spreadsheet,
        rewrite_with_ai: false
      };

      // Log the API request details
      console.log('Making API Request:', {
        url: `https://plansync-backend-234288438538.us-central1.run.app/api/requirements/${year}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
          'accept': 'application/json'
        },
        body: transformedData
      });

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/requirements/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
          'accept': 'application/json'
        },
        body: JSON.stringify(transformedData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Success Response:', data);
      message.success('Requirement added successfully');

      // Reset form after successful submission
      setFormData({
        company_name: '',
        quarter: '',
        reporting_year: 'All',
        requirement: '',
        stakeholder: '',
        completed: 'No',
        due_dates: {},
        priority: 'Medium',
        add_to_annual_plan_review: 'No',
        include_in_next_years_master_spreadsheet: 'No'
      });

      // Call callbacks if provided
      if (onAddRow) {
        onAddRow({
          CompanyName: formData.company_name,
          Quarter: formData.quarter,
          ReportingYear: formData.reporting_year,
          Requirement: formData.requirement,
          Stakeholder: formData.stakeholder,
          Complete: formData.completed,
          DueDate: JSON.stringify(formData.due_dates),
          Priority: formData.priority
        });
      }

      if (onDataSubmitted) {
        onDataSubmitted();
      }
    } catch (error) {
      console.error('Error submitting requirement:', error);
      message.error(error.message || 'Failed to submit requirement');
    }
  };

  // Add this function to check missing fields
  const checkMissingFields = () => {
    const missingFields = [];
    
    if (!formData.company_name) missingFields.push('Company Name');
    if (!formData.quarter) missingFields.push('Quarter');
    if (!formData.reporting_year) missingFields.push('Reporting Year');
    if (!formData.requirement.trim()) missingFields.push('Requirement');
    if (!formData.stakeholder) missingFields.push('Stakeholder');
    if (Object.keys(formData.due_dates).length === 0) missingFields.push('Due Date');
    if (!formData.company_name === 'All Clients' && !formData.completed) missingFields.push('Complete Status');

    return missingFields;
  };

  // Update the isFormValid function
  const isFormValid = () => {
    const baseValidation = formData.company_name &&
      formData.quarter &&
      formData.reporting_year &&
      formData.requirement.trim() !== '' &&
      formData.stakeholder &&
      Object.keys(formData.due_dates).length === 1;

    const validationDetails = {
      company_name: Boolean(formData.company_name),
      quarter: Boolean(formData.quarter),
      reporting_year: Boolean(formData.reporting_year),
      requirement: Boolean(formData.requirement.trim()),
      stakeholder: Boolean(formData.stakeholder),
      due_dates: Object.keys(formData.due_dates).length === 1,
      completed: formData.completed
    };

    console.log('Form Validation Details:', validationDetails);
    
    // Log missing fields
    const missingFields = checkMissingFields();
    if (missingFields.length > 0) {
      console.log('Missing Required Fields:', missingFields);
    }

    if (formData.company_name === 'All Clients') {
      console.log('All Clients validation result:', baseValidation);
      return baseValidation;
    }

    const result = baseValidation && formData.completed;
    console.log('Specific company validation result:', result);
    return result;
  };

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
      </Tooltip>
    </div>
  );

  // Update the requirement input section in the render
  const requirementSection = (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {renderLabel("Requirement", "Describe the specific requirement or task that needs to be completed.")}
      </label>
      <div className="space-y-2">
        <div className="relative">
          <Spin spinning={isRewriting} tip="AI is rewriting your text...">
            <div className={`transition-opacity duration-300 ${isRewriting ? 'opacity-50' : 'opacity-100'}`}>
              <TextArea
                value={formData.requirement}
                onChange={(e) => handleChange('requirement', e.target.value)}
                rows={3}
                placeholder="Enter requirement details"
                className="w-full"
                disabled={isRewriting}
              />
            </div>
          </Spin>
        </div>
        <Button 
          onClick={handleRewriteText}
          icon={<SparklesIcon className={`h-5 w-5 mr-2 ${isRewriting ? 'animate-spin' : ''}`} />}
          className={`
            inline-flex items-center px-4 py-2 border border-transparent 
            text-sm font-medium rounded-md shadow-sm text-white 
            ${isRewriting ? 'bg-indigo-500' : formData.requirement.trim() ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed'} 
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
            transition-all duration-200
          `}
          disabled={isRewriting || !formData.requirement.trim()}
        >
          {isRewriting ? 'Rewriting...' : 'Rewrite with AI'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-2 bg-white rounded-2xl">
      <h1 className="text-3xl font-light text-gray-800 mb-6">Requirements Checklist Data Entry</h1>
      <form className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Company Name", "Select a company to apply these requirements to.")}
            </label>
            <Select
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              value={formData.company_name}
              onChange={(value) => handleChange('company_name', value)}
              placeholder="Select a company"
            >
              {companyOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Quarter", "Select the quarter these requirements apply to.")}
            </label>
            <Select
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              value={formData.quarter}
              onChange={(value) => handleChange('quarter', value)}
              placeholder="Select a quarter"
            >
              {quarterOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Reporting Year", "Select the year these requirements apply to for reporting.")}
          </label>
          <Select
            style={{ width: '100%' }}
            value={formData.reporting_year}
            onChange={(value) => handleChange('reporting_year', value)}
          >
            <Option value="All">All</Option>
            {[dayjs().year() - 2, dayjs().year() - 1, dayjs().year()].map(year => (
              <Option key={year.toString()} value={year.toString()}>{year}</Option>
            ))}
          </Select>
        </div>

        {requirementSection}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Stakeholder", "Select the party involved in completing this requirement.")}
          </label>
          <Select
            showSearch
            optionFilterProp="children"
            style={{ width: '100%' }}
            value={formData.stakeholder}
            onChange={(value) => handleChange('stakeholder', value)}
            placeholder="Select a stakeholder"
          >
            {stakeholderOptions.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </div>

        {formData.quarter && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {renderLabel(`Due Date for ${formData.quarter}`, "Set the deadline for completing this requirement.")}
              </label>
              <Input
                type="date"
                value={formData.due_dates[formData.quarter] || ''}
                onChange={(e) => handleDueDateChange(formData.quarter, e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Complete", "Indicate the current status of this requirement.")}
            </label>
            <Select
              value={formData.completed}
              onChange={(value) => handleChange('completed', value)}
              style={{ width: '100%' }}
              disabled={formData.company_name === 'All Clients'}
              title={formData.company_name === 'All Clients' ? 'Automatically set to No for All Clients requirements' : ''}
            >
              <Option value="Yes">Yes</Option>
              <Option value="No">No</Option>
              <Option value="Not Required">Not Required</Option>
              <Option value="Submitted">Submitted</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Priority", "Mark if this requirement needs immediate attention.")}
            </label>
            <Select
              value={formData.priority}
              onChange={(value) => handleChange('priority', value)}
              style={{ width: '100%' }}
            >
              <Option value="High">High</Option>
              <Option value="Medium">Medium</Option>
              <Option value="Low">Low</Option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Include in Annual Review Report", "Select to include this requirement in the year-end review.")}
            </label>
            <Select
              value={formData.add_to_annual_plan_review}
              onChange={(value) => handleChange('add_to_annual_plan_review', value)}
              style={{ width: '100%' }}
            >
              <Option value="Yes">Yes</Option>
              <Option value="No">No</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Include in Next Year's Plan Data", "Choose to carry this requirement forward to next year's planning.")}
            </label>
            <Select
              value={formData.include_in_next_years_master_spreadsheet}
              onChange={(value) => handleChange('include_in_next_years_master_spreadsheet', value)}
              style={{ width: '100%' }}
            >
              <Option value="Yes">Yes</Option>
              <Option value="No">No</Option>
            </Select>
          </div>
        </div>

        <div className="flex justify-start">
          <Tooltip title="Save this requirement to the checklist">
            <Button 
              onClick={handleSubmit}
              disabled={!isFormValid()}
              size="large"
              className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white ${isFormValid() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
            >
              <DocumentIcon className="h-5 w-5 mr-2" />
              Submit Requirements
            </Button>
          </Tooltip>
        </div>
      </form>
    </div>
  );
};

export default AutomaticDataEntryRequirementsChecklist;